import { useEffect, useMemo, useState } from "react";
import { Button, Card, Empty, Form, Input, Modal, Select, Space, Table, Tabs, Tag, Typography, message } from "antd";
import { Bell, CheckCircle, Clock, Plus, Timer, TrendingUp, Users, Zap } from "lucide-react";
import dayjs from "dayjs";
import axiosClient from "../../api/axiosClient";

const { Title, Text } = Typography;

interface WaitlistEntry {
  waitlist_id: string;
  client_name: string;
  client_phone: string;
  service: string;
  staff_id?: string | null;
  preferred_days: string;
  status: string;
  created_at: string;
}

interface SlotNotification {
  notification_id: string;
  waitlist_id: string;
  client_name: string;
  client_phone: string;
  staff_id?: string | null;
  service: string;
  slot_start: string;
  slot_end: string;
  source_booking_id?: string | null;
  position: number;
  sent_at: string;
  expires_at: string;
  outcome: "sent" | "accepted" | "expired";
  responded_at?: string | null;
}

interface WaitlistSettings {
  urgency_hours: number;
  urgent_window_mins: number;
  normal_window_mins: number;
}

interface Staff {
  user_id: string;
  name: string;
}

function useCountdown(expiresAt: string) {
  const [remaining, setRemaining] = useState(() => Math.max(0, dayjs(expiresAt).diff(dayjs(), "second")));
  useEffect(() => {
    const t = setInterval(() => setRemaining(Math.max(0, dayjs(expiresAt).diff(dayjs(), "second"))), 1000);
    return () => clearInterval(t);
  }, [expiresAt]);
  const mins = Math.floor(remaining / 60);
  const secs = remaining % 60;
  return `${mins}:${String(secs).padStart(2, "0")}`;
}

function CountdownBadge({ expiresAt }: { expiresAt: string }) {
  const label = useCountdown(expiresAt);
  return <Tag color="processing" icon={<Timer size={11} />}>{label} left</Tag>;
}

export default function AdminWaitlistManagerPage() {
  const [queue, setQueue] = useState<WaitlistEntry[]>([]);
  const [notifications, setNotifications] = useState<SlotNotification[]>([]);
  const [settings, setSettings] = useState<WaitlistSettings | null>(null);
  const [staff, setStaff] = useState<Staff[]>([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [form] = Form.useForm();

  function fetchAll() {
    setLoading(true);
    Promise.all([
      axiosClient.get("/waitlist"),
      axiosClient.get("/waitlist/notifications"),
      axiosClient.get("/waitlist/settings"),
      axiosClient.get("/staff"),
    ])
      .then(([q, n, s, st]) => {
        setQueue(q.data ?? []);
        setNotifications(n.data ?? []);
        setSettings(s.data);
        setStaff(st.data ?? []);
      })
      .catch(() => message.error("Failed to load waitlist"))
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    fetchAll();
    // Poll while this page is open so timed-out cascades actually advance —
    // there's no background worker in this codebase, see go_db/waitlist_cascade.go.
    const t = setInterval(() => {
      axiosClient.get("/waitlist/notifications").then((res) => setNotifications(res.data ?? [])).catch(() => {});
    }, 5000);
    return () => clearInterval(t);
  }, []);

  async function addToWaitlist() {
    const vals = await form.validateFields();
    try {
      await axiosClient.post("/waitlist", vals);
      message.success("Added to waitlist");
      setAddOpen(false);
      form.resetFields();
      fetchAll();
    } catch {
      message.error("Failed to add to waitlist");
    }
  }

  async function saveSettings() {
    if (!settings) return;
    setSavingSettings(true);
    try {
      await axiosClient.put("/waitlist/settings", settings);
      message.success("Settings saved");
    } catch {
      message.error("Failed to save settings");
    } finally {
      setSavingSettings(false);
    }
  }

  // Active cascades: the most recent notification per source booking that's still "sent".
  const activeCascades = notifications.filter((n) => n.outcome === "sent");
  const groupedHistory = useMemo(() => {
    const groups: Record<string, SlotNotification[]> = {};
    notifications.forEach((n) => {
      const key = n.source_booking_id ?? n.waitlist_id;
      (groups[key] ??= []).push(n);
    });
    return Object.values(groups);
  }, [notifications]);

  const waitingCount = queue.filter((e) => e.status === "waiting").length;
  const filledCount = notifications.filter((n) => n.outcome === "accepted").length;
  const totalSent = notifications.length;
  const responseRate = totalSent > 0 ? Math.round((filledCount / totalSent) * 100) : 0;

  const staffName = (id?: string | null) => staff.find((s) => s.user_id === id)?.name ?? "Any";

  const liveTab = (
    <div>
      {activeCascades.length === 0 ? (
        <Empty description="No active cascades right now — one starts automatically whenever an appointment is cancelled and a matching client is waiting" />
      ) : (
        <Space direction="vertical" size={12} style={{ width: "100%" }}>
          {activeCascades.map((n) => (
            <Card key={n.notification_id} size="small">
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                <div>
                  <Space>
                    <Text strong>{n.service}</Text>
                    <Tag color="blue">Notifying…</Tag>
                  </Space>
                  <div style={{ fontSize: 13, color: "#595959", marginTop: 4 }}>
                    {staffName(n.staff_id)} · {dayjs(n.slot_start).format("MMM D, h:mm A")}
                  </div>
                  <div style={{ fontSize: 13, marginTop: 6 }}>
                    Texted <Text strong>{n.client_name}</Text> ({n.client_phone}) — position {n.position} in queue
                  </div>
                </div>
                <CountdownBadge expiresAt={n.expires_at} />
              </div>
            </Card>
          ))}
        </Space>
      )}
    </div>
  );

  const queueTab = (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 16 }}>
        <Text type="secondary">{waitingCount} clients currently waiting</Text>
        <Button type="primary" icon={<Plus size={14} />} onClick={() => { form.resetFields(); setAddOpen(true); }}>
          Add to Waitlist
        </Button>
      </div>
      <Table
        rowKey="waitlist_id"
        loading={loading}
        dataSource={queue}
        columns={[
          { title: "Client", dataIndex: "client_name", render: (v: string, e: WaitlistEntry) => <div><div>{v}</div><Text type="secondary" style={{ fontSize: 12 }}>{e.client_phone}</Text></div> },
          { title: "Service", dataIndex: "service" },
          { title: "Staff Pref.", dataIndex: "staff_id", render: (id?: string | null) => staffName(id) },
          { title: "Availability", dataIndex: "preferred_days", render: (v: string) => v || "Anytime" },
          { title: "Added", dataIndex: "created_at", render: (v: string) => dayjs(v).format("MMM D, h:mm A") },
          { title: "Status", dataIndex: "status", render: (v: string) => <Tag color={v === "waiting" ? "blue" : "green"}>{v}</Tag> },
        ]}
      />
    </div>
  );

  const historyTab = (
    <Space direction="vertical" size={16} style={{ width: "100%" }}>
      {groupedHistory.length === 0 && <Empty description="No cascade activity yet" />}
      {groupedHistory.map((group, i) => (
        <Card key={i} size="small" title={`${group[0].service} — ${dayjs(group[0].slot_start).format("MMM D, h:mm A")}`}>
          <Space direction="vertical" size={8} style={{ width: "100%" }}>
            {group.map((n) => (
              <div key={n.notification_id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div>
                  <Text strong>{n.client_name}</Text>
                  <Text type="secondary" style={{ fontSize: 12, marginLeft: 8 }}>{n.client_phone} · Texted {dayjs(n.sent_at).format("h:mm A")}</Text>
                </div>
                <Tag
                  color={n.outcome === "accepted" ? "success" : n.outcome === "expired" ? "warning" : "processing"}
                  icon={n.outcome === "accepted" ? <CheckCircle size={11} /> : <Clock size={11} />}
                >
                  {n.outcome === "accepted" ? "Accepted" : n.outcome === "expired" ? "Timed Out" : "Notifying"}
                </Tag>
              </div>
            ))}
          </Space>
        </Card>
      ))}
    </Space>
  );

  const settingsTab = settings && (
    <div style={{ maxWidth: 560 }}>
      <Card size="small" title="Timing Rules">
        <Space direction="vertical" size={20} style={{ width: "100%" }}>
          <div>
            <Text style={{ fontWeight: 500 }}>Urgency Window</Text>
            <div style={{ fontSize: 12, color: "#9ca3af", marginBottom: 8 }}>Slots opening sooner than this get the shorter per-client countdown.</div>
            <Space>
              {[1, 2, 3, 4].map((h) => (
                <Button key={h} type={settings.urgency_hours === h ? "primary" : "default"} onClick={() => setSettings({ ...settings, urgency_hours: h })}>{h}h</Button>
              ))}
            </Space>
          </div>
          <div>
            <Text style={{ fontWeight: 500 }}>Urgent Per-Client Countdown</Text>
            <div style={{ fontSize: 12, color: "#9ca3af", marginBottom: 8 }}>How long each client has before the next person is texted.</div>
            <Space>
              {[5, 10, 15, 20].map((m) => (
                <Button key={m} type={settings.urgent_window_mins === m ? "primary" : "default"} onClick={() => setSettings({ ...settings, urgent_window_mins: m })}>{m}m</Button>
              ))}
            </Space>
          </div>
          <div>
            <Text style={{ fontWeight: 500 }}>Standard Window</Text>
            <div style={{ fontSize: 12, color: "#9ca3af", marginBottom: 8 }}>For non-urgent slots, magic link validity before auto-advancing.</div>
            <Space>
              {[15, 30, 45, 60].map((m) => (
                <Button key={m} type={settings.normal_window_mins === m ? "primary" : "default"} onClick={() => setSettings({ ...settings, normal_window_mins: m })}>{m}m</Button>
              ))}
            </Space>
          </div>
        </Space>
      </Card>
      <Button type="primary" style={{ marginTop: 16 }} loading={savingSettings} onClick={saveSettings}>Save Settings</Button>
    </div>
  );

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
        <div style={{ width: 36, height: 36, borderRadius: 10, background: "#dbeafe", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Zap size={18} color="#2563eb" />
        </div>
        <div>
          <Title level={3} style={{ margin: 0 }}>Automated Waitlist</Title>
          <Text type="secondary" style={{ fontSize: 12 }}>Texts the next waitlisted client the moment an appointment is cancelled — first come, first served</Text>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginBottom: 16, maxWidth: 720 }}>
        <Card size="small"><Space size={8}><Users size={14} color="#2563eb" /><div><div style={{ fontSize: 18, fontWeight: 700 }}>{waitingCount}</div><Text type="secondary" style={{ fontSize: 11 }}>Waiting</Text></div></Space></Card>
        <Card size="small"><Space size={8}><Bell size={14} color="#d97706" /><div><div style={{ fontSize: 18, fontWeight: 700 }}>{activeCascades.length}</div><Text type="secondary" style={{ fontSize: 11 }}>Active Cascades</Text></div></Space></Card>
        <Card size="small"><Space size={8}><CheckCircle size={14} color="#059669" /><div><div style={{ fontSize: 18, fontWeight: 700 }}>{filledCount}</div><Text type="secondary" style={{ fontSize: 11 }}>Filled</Text></div></Space></Card>
        <Card size="small"><Space size={8}><TrendingUp size={14} color="#7c3aed" /><div><div style={{ fontSize: 18, fontWeight: 700 }}>{responseRate}%</div><Text type="secondary" style={{ fontSize: 11 }}>Response Rate</Text></div></Space></Card>
      </div>

      <Tabs
        items={[
          { key: "live", label: "Live Queue", children: liveTab },
          { key: "queue", label: "Waitlist", children: queueTab },
          { key: "history", label: "Activity Log", children: historyTab },
          { key: "settings", label: "Settings", children: settingsTab },
        ]}
      />

      <Modal title="Add to Waitlist" open={addOpen} onCancel={() => setAddOpen(false)} onOk={addToWaitlist} okText="Add">
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item name="client_name" label="Client Name" rules={[{ required: true, message: "Required" }]}>
            <Input placeholder="Full name" />
          </Form.Item>
          <Form.Item name="client_phone" label="Phone">
            <Input placeholder="(555) 000-0000" />
          </Form.Item>
          <Form.Item name="service" label="Service" rules={[{ required: true, message: "Required" }]}>
            <Input placeholder="e.g. Haircut & Style" />
          </Form.Item>
          <Form.Item name="staff_id" label="Staff Preference">
            <Select allowClear placeholder="Any available staff" options={staff.map((s) => ({ value: s.user_id, label: s.name }))} />
          </Form.Item>
          <Form.Item name="preferred_days" label="Preferred Availability">
            <Input placeholder="e.g. Weekdays, Anytime" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
